import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Button,
    Card,
    Col,
    Divider,
    Drawer,
    Empty,
    Form,
    Input,
    InputNumber,
    Modal,
    Popconfirm,
    Radio,
    Row,
    Select,
    Space,
    Switch,
    Table,
    Tabs,
    Tag,
    Typography,
    Upload,
    message
} from 'antd';
import {
    DeleteOutlined,
    EditOutlined,
    PlusOutlined,
    ReloadOutlined,
    UploadOutlined
} from '@ant-design/icons';
import Layout from './Layout';
import { tmApi } from '../utils/taskManager';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const SCHEMA_TYPES = [
    { value: 'string', label: 'Строка' },
    { value: 'text', label: 'Текст' },
    { value: 'number', label: 'Число' },
    { value: 'date', label: 'Дата' },
    { value: 'datetime', label: 'Дата и время' },
    { value: 'boolean', label: 'Да / Нет' },
    { value: 'select', label: 'Список' }
];

const ROLE_OPTIONS = [
    { value: 'value', label: 'Код (value)' },
    { value: 'label', label: 'Название (label)' },
    { value: 'data', label: 'Данные' }
];

const DEFAULT_SCHEMA = [
    {
        key: 'value',
        label: 'Код',
        value_type: 'string',
        required: true,
        role: 'value',
        possible_values: ''
    },
    {
        key: 'label',
        label: 'Название',
        value_type: 'string',
        required: true,
        role: 'label',
        possible_values: ''
    }
];

const normalizeSchemaForForm = fields =>
    (Array.isArray(fields) && fields.length
        ? fields
        : DEFAULT_SCHEMA
    ).map(item => ({
        ...item,
        possible_values: Array.isArray(item.possible_values)
            ? item.possible_values.join('\n')
            : item.possible_values || ''
    }));

const serializeSchemaFromForm = fields =>
    (fields || [])
        .map(item => {
            if (!item?.key) return null;
            return {
                key: String(item.key)
                    .trim()
                    .replace(/\s+/g, '_'),
                label: String(item.label || item.key).trim(),
                value_type: item.value_type || 'string',
                required: Boolean(item.required),
                role: item.role || 'data',
                possible_values: String(
                    item.possible_values || ''
                )
                    .split('\n')
                    .map(v => v.trim())
                    .filter(Boolean)
            };
        })
        .filter(Boolean);

const SchemaFieldsEditor = ({ name = 'schema_fields' }) => (
    <Form.List name={name}>
        {(fields, { add, remove }) => (
            <Space direction="vertical" style={{ width: '100%' }}>
                {fields.map(field => (
                    <Card
                        key={field.key}
                        size="small"
                        title={`Поле #${field.name + 1}`}
                        extra={
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => remove(field.name)}
                            />
                        }
                    >
                        <Row gutter={8}>
                            <Col xs={24} sm={8}>
                                <Form.Item
                                    {...field}
                                    name={[field.name, 'key']}
                                    label="Ключ"
                                    rules={[{ required: true }]}
                                >
                                    <Input placeholder="code" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={8}>
                                <Form.Item
                                    {...field}
                                    name={[field.name, 'label']}
                                    label="Название"
                                    rules={[{ required: true }]}
                                >
                                    <Input placeholder="Код" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={8}>
                                <Form.Item
                                    {...field}
                                    name={[field.name, 'value_type']}
                                    label="Тип"
                                    initialValue="string"
                                >
                                    <Select options={SCHEMA_TYPES} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={8}>
                                <Form.Item
                                    {...field}
                                    name={[field.name, 'role']}
                                    label="Роль"
                                    initialValue="data"
                                >
                                    <Select options={ROLE_OPTIONS} />
                                </Form.Item>
                            </Col>
                            <Col xs={12} sm={4}>
                                <Form.Item
                                    {...field}
                                    name={[field.name, 'required']}
                                    label="Обяз."
                                    valuePropName="checked"
                                >
                                    <Switch />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Form.Item
                                    noStyle
                                    shouldUpdate
                                >
                                    {() => null}
                                </Form.Item>
                                <Form.Item
                                    {...field}
                                    name={[
                                        field.name,
                                        'possible_values'
                                    ]}
                                    label="Опции select (по строке)"
                                >
                                    <TextArea rows={2} />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>
                ))}
                <Button
                    type="dashed"
                    block
                    icon={<PlusOutlined />}
                    onClick={() =>
                        add({
                            value_type: 'string',
                            role: 'data',
                            required: false,
                            possible_values: ''
                        })
                    }
                >
                    Добавить поле сущности
                </Button>
            </Space>
        )}
    </Form.List>
);

const RecordFieldInput = ({ field }) => {
    if (field.value_type === 'boolean') {
        return <Switch />;
    }

    if (field.value_type === 'number') {
        return <InputNumber style={{ width: '100%' }} />;
    }

    if (field.value_type === 'text') {
        return <TextArea rows={2} />;
    }

    if (field.value_type === 'select') {
        return (
            <Select
                allowClear
                options={(field.possible_values || []).map(
                    value => ({ value, label: value })
                )}
            />
        );
    }

    if (
        field.value_type === 'date' ||
        field.value_type === 'datetime'
    ) {
        return <Input placeholder="YYYY-MM-DD" />;
    }

    return <Input />;
};

const TaskManagerDictionariesPage = () => {
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [selectedId, setSelectedId] = useState(null);

    const [entityOpen, setEntityOpen] = useState(false);
    const [editingEntity, setEditingEntity] = useState(null);
    const [entityForm] = Form.useForm();

    const [items, setItems] = useState([]);
    const [itemsLoading, setItemsLoading] = useState(false);
    const [activeDict, setActiveDict] = useState(null);

    const [recordOpen, setRecordOpen] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [recordForm] = Form.useForm();

    const [importOpen, setImportOpen] = useState(false);
    const [importText, setImportText] = useState('');
    const [importMode, setImportMode] = useState('append');
    const [importing, setImporting] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await tmApi.get(
                '/api/task-manager/dictionaries'
            );
            setRows(data.data || []);
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Ошибка загрузки справочников'
            );
        } finally {
            setLoading(false);
        }
    }, []);

    const loadItems = useCallback(async dictId => {
        if (!dictId) {
            setItems([]);
            setActiveDict(null);
            return;
        }

        setItemsLoading(true);
        try {
            const { data } = await tmApi.get(
                `/api/task-manager/dictionaries/${dictId}/items`,
                { params: { all: 1, slim: 1 } }
            );
            setItems(data.data || []);
            setActiveDict(data.dictionary || null);
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Ошибка загрузки данных'
            );
        } finally {
            setItemsLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (selectedId) {
            loadItems(selectedId);
        }
    }, [selectedId, loadItems]);

    const selected = useMemo(
        () => rows.find(item => item.id === selectedId) || activeDict,
        [rows, selectedId, activeDict]
    );

    const schemaFields = useMemo(
        () =>
            normalizeSchemaForForm(
                selected?.schema_fields || DEFAULT_SCHEMA
            ).map(item => ({
                ...item,
                possible_values: String(item.possible_values || '')
                    .split('\n')
                    .map(v => v.trim())
                    .filter(Boolean)
            })),
        [selected]
    );

    const openCreateEntity = () => {
        setEditingEntity(null);
        setEntityOpen(true);
    };

    const openEditEntity = record => {
        setEditingEntity(record);
        setEntityOpen(true);
    };

    useEffect(() => {
        if (!entityOpen) return;
        if (editingEntity) {
            entityForm.setFieldsValue({
                ...editingEntity,
                schema_fields: normalizeSchemaForForm(
                    editingEntity.schema_fields
                )
            });
        } else {
            entityForm.resetFields();
            entityForm.setFieldsValue({
                is_active: true,
                sort_order: 100,
                schema_fields: DEFAULT_SCHEMA
            });
        }
    }, [entityOpen, editingEntity, entityForm]);

    const saveEntity = async () => {
        try {
            const values = await entityForm.validateFields();
            const payload = {
                ...values,
                schema_fields: serializeSchemaFromForm(
                    values.schema_fields
                )
            };

            if (!payload.schema_fields.length) {
                message.error('Добавьте хотя бы одно поле');
                return;
            }

            if (editingEntity) {
                await tmApi.put(
                    `/api/task-manager/dictionaries/${editingEntity.id}`,
                    payload
                );
                message.success('Сущность обновлена');
            } else {
                const { data } = await tmApi.post(
                    '/api/task-manager/dictionaries',
                    payload
                );
                message.success('Сущность создана');
                setSelectedId(data.data?.id || null);
            }

            setEntityOpen(false);
            await load();
            if (selectedId) {
                await loadItems(selectedId);
            }
        } catch (error) {
            if (error?.errorFields) return;
            message.error(
                error?.response?.data?.message ||
                    'Ошибка сохранения'
            );
        }
    };

    const removeEntity = async id => {
        try {
            await tmApi.delete(
                `/api/task-manager/dictionaries/${id}`
            );
            message.success('Сущность удалена');
            if (selectedId === id) {
                setSelectedId(null);
                setItems([]);
                setActiveDict(null);
            }
            load();
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Ошибка удаления'
            );
        }
    };

    const openCreateRecord = () => {
        setEditingRecord(null);
        setRecordOpen(true);
    };

    const openEditRecord = record => {
        setEditingRecord(record);
        setRecordOpen(true);
    };

    useEffect(() => {
        if (!recordOpen) return;
        if (editingRecord) {
            const data = {
                ...(editingRecord.data ||
                    editingRecord.meta ||
                    {}),
                is_active: editingRecord.is_active !== false,
                sort_order: editingRecord.sort_order
            };
            schemaFields.forEach(field => {
                if (data[field.key] === undefined) {
                    if (field.role === 'value') {
                        data[field.key] = editingRecord.value;
                    }
                    if (field.role === 'label') {
                        data[field.key] = editingRecord.label;
                    }
                }
            });
            recordForm.resetFields();
            recordForm.setFieldsValue(data);
        } else {
            recordForm.resetFields();
            const initial = {};
            schemaFields.forEach(field => {
                if (field.value_type === 'boolean') {
                    initial[field.key] = false;
                }
            });
            recordForm.setFieldsValue(initial);
        }
    }, [recordOpen, editingRecord, recordForm, schemaFields]);

    const saveRecord = async () => {
        try {
            const values = await recordForm.validateFields();
            const payload = {
                data: values,
                is_active: values.is_active !== false,
                sort_order: values.sort_order
            };

            if (editingRecord) {
                await tmApi.put(
                    `/api/task-manager/dictionaries/${selectedId}/items/${editingRecord.id}`,
                    payload
                );
                message.success('Запись обновлена');
            } else {
                await tmApi.post(
                    `/api/task-manager/dictionaries/${selectedId}/items`,
                    payload
                );
                message.success('Запись создана');
            }

            setRecordOpen(false);
            loadItems(selectedId);
            load();
        } catch (error) {
            if (error?.errorFields) return;
            message.error(
                error?.response?.data?.message ||
                    'Ошибка сохранения записи'
            );
        }
    };

    const removeRecord = async id => {
        try {
            await tmApi.delete(
                `/api/task-manager/dictionaries/${selectedId}/items/${id}`
            );
            message.success('Запись удалена');
            loadItems(selectedId);
            load();
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Ошибка удаления'
            );
        }
    };

    const runImport = async () => {
        if (!importText.trim()) {
            message.warning('Вставьте данные или загрузите файл');
            return;
        }

        setImporting(true);
        try {
            const { data } = await tmApi.post(
                `/api/task-manager/dictionaries/${selectedId}/import`,
                {
                    text: importText,
                    mode: importMode
                }
            );
            message.success(
                `Импортировано: ${data.imported || 0}` +
                    (data.errors?.length
                        ? `, ошибок: ${data.errors.length}`
                        : '')
            );
            setImportOpen(false);
            setImportText('');
            loadItems(selectedId);
            load();
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Ошибка импорта'
            );
        } finally {
            setImporting(false);
        }
    };

    const entityColumns = [
        {
            title: 'Сущность',
            render: (_, row) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{row.human_name}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {row.system_name}
                    </Text>
                </Space>
            )
        },
        {
            title: 'Полей',
            width: 80,
            render: (_, row) =>
                (row.schema_fields || []).length || 0
        },
        {
            title: 'Записей',
            dataIndex: 'items_count',
            width: 90
        },
        {
            title: '',
            width: 110,
            render: (_, row) => (
                <Space>
                    <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={e => {
                            e.stopPropagation();
                            openEditEntity(row);
                        }}
                    />
                    <Popconfirm
                        title="Удалить сущность?"
                        onConfirm={e => {
                            e?.stopPropagation?.();
                            removeEntity(row.id);
                        }}
                    >
                        <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={e => e.stopPropagation()}
                        />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const dataColumns = [
        ...schemaFields.map(field => ({
            title: (
                <Space size={4}>
                    <span>{field.label}</span>
                    {field.role === 'value' ? (
                        <Tag color="blue">код</Tag>
                    ) : null}
                    {field.role === 'label' ? (
                        <Tag color="green">имя</Tag>
                    ) : null}
                </Space>
            ),
            dataIndex: ['data', field.key],
            render: (value, row) => {
                const data = row.data || row.meta || {};
                let cell = data[field.key];
                if (cell == null) {
                    if (field.role === 'value') cell = row.value;
                    if (field.role === 'label') cell = row.label;
                }
                if (typeof cell === 'boolean') {
                    return cell ? 'да' : 'нет';
                }
                return cell != null && cell !== ''
                    ? String(cell)
                    : '—';
            }
        })),
        {
            title: '',
            width: 100,
            fixed: 'right',
            render: (_, row) => (
                <Space>
                    <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => openEditRecord(row)}
                    />
                    <Popconfirm
                        title="Удалить запись?"
                        onConfirm={() => removeRecord(row.id)}
                    >
                        <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                        />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const csvHint = schemaFields.map(f => f.key).join(',');

    return (
        <Layout>
            <div style={{ padding: 16 }}>
                <Space
                    style={{
                        width: '100%',
                        justifyContent: 'space-between',
                        marginBottom: 16
                    }}
                    wrap
                >
                    <div>
                        <Title level={3} style={{ margin: 0 }}>
                            Справочники / сущности
                        </Title>
                        <Text type="secondary">
                            Создайте сущность с полями, заполните
                            данные вручную или загрузите CSV
                        </Text>
                    </div>
                    <Space wrap>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={load}
                        >
                            Обновить
                        </Button>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={openCreateEntity}
                        >
                            Новая сущность
                        </Button>
                    </Space>
                </Space>

                <Row gutter={16}>
                    <Col xs={24} lg={8}>
                        <Card
                            size="small"
                            title="Сущности"
                            styles={{ body: { padding: 0 } }}
                        >
                            <Table
                                rowKey="id"
                                size="small"
                                loading={loading}
                                dataSource={rows}
                                columns={entityColumns}
                                pagination={{ pageSize: 12 }}
                                onRow={record => ({
                                    onClick: () =>
                                        setSelectedId(record.id),
                                    style: {
                                        cursor: 'pointer',
                                        background:
                                            selectedId ===
                                            record.id
                                                ? '#e6f4ff'
                                                : undefined
                                    }
                                })}
                            />
                        </Card>
                    </Col>

                    <Col xs={24} lg={16}>
                        {!selectedId ? (
                            <Card>
                                <Empty description="Выберите сущность слева или создайте новую" />
                            </Card>
                        ) : (
                            <Card
                                title={
                                    <Space direction="vertical" size={0}>
                                        <Text strong>
                                            {selected?.human_name}
                                        </Text>
                                        <Text
                                            type="secondary"
                                            style={{
                                                fontSize: 12,
                                                fontWeight: 400
                                            }}
                                        >
                                            {selected?.system_name}
                                            {selected?.description
                                                ? ` — ${selected.description}`
                                                : ''}
                                        </Text>
                                    </Space>
                                }
                                extra={
                                    <Space wrap>
                                        <Button
                                            icon={
                                                <EditOutlined />
                                            }
                                            onClick={() =>
                                                openEditEntity(
                                                    selected
                                                )
                                            }
                                        >
                                            Схема
                                        </Button>
                                        <Button
                                            icon={
                                                <UploadOutlined />
                                            }
                                            onClick={() =>
                                                setImportOpen(true)
                                            }
                                        >
                                            Загрузить
                                        </Button>
                                        <Button
                                            type="primary"
                                            icon={
                                                <PlusOutlined />
                                            }
                                            onClick={
                                                openCreateRecord
                                            }
                                        >
                                            Запись
                                        </Button>
                                    </Space>
                                }
                            >
                                <Tabs
                                    items={[
                                        {
                                            key: 'data',
                                            label: `Данные (${items.length})`,
                                            children: (
                                                <Table
                                                    rowKey="id"
                                                    size="small"
                                                    loading={
                                                        itemsLoading
                                                    }
                                                    dataSource={
                                                        items
                                                    }
                                                    columns={
                                                        dataColumns
                                                    }
                                                    scroll={{
                                                        x: true
                                                    }}
                                                    pagination={{
                                                        pageSize: 20
                                                    }}
                                                />
                                            )
                                        },
                                        {
                                            key: 'schema',
                                            label: `Поля (${schemaFields.length})`,
                                            children: (
                                                <Table
                                                    rowKey="key"
                                                    size="small"
                                                    pagination={
                                                        false
                                                    }
                                                    dataSource={
                                                        schemaFields
                                                    }
                                                    columns={[
                                                        {
                                                            title: 'Ключ',
                                                            dataIndex:
                                                                'key'
                                                        },
                                                        {
                                                            title: 'Название',
                                                            dataIndex:
                                                                'label'
                                                        },
                                                        {
                                                            title: 'Тип',
                                                            dataIndex:
                                                                'value_type'
                                                        },
                                                        {
                                                            title: 'Роль',
                                                            dataIndex:
                                                                'role',
                                                            render: v =>
                                                                ROLE_OPTIONS.find(
                                                                    o =>
                                                                        o.value ===
                                                                        v
                                                                )
                                                                    ?.label ||
                                                                v
                                                        },
                                                        {
                                                            title: 'Обяз.',
                                                            dataIndex:
                                                                'required',
                                                            render: v =>
                                                                v
                                                                    ? 'да'
                                                                    : 'нет'
                                                        }
                                                    ]}
                                                />
                                            )
                                        }
                                    ]}
                                />
                            </Card>
                        )}
                    </Col>
                </Row>

                <Drawer
                    open={entityOpen}
                    title={
                        editingEntity
                            ? `Сущность #${editingEntity.id}`
                            : 'Новая сущность'
                    }
                    width={720}
                    onClose={() => setEntityOpen(false)}
                    destroyOnClose
                    extra={
                        <Button type="primary" onClick={saveEntity}>
                            Сохранить
                        </Button>
                    }
                >
                    <Form form={entityForm} layout="vertical">
                        <Form.Item
                            name="system_name"
                            label="System name"
                            rules={[{ required: true }]}
                        >
                            <Input
                                disabled={Boolean(editingEntity)}
                                placeholder="regions"
                            />
                        </Form.Item>
                        <Form.Item
                            name="human_name"
                            label="Название сущности"
                            rules={[{ required: true }]}
                        >
                            <Input placeholder="Регионы" />
                        </Form.Item>
                        <Form.Item
                            name="description"
                            label="Описание"
                        >
                            <TextArea rows={2} />
                        </Form.Item>
                        <Form.Item
                            name="sort_order"
                            label="Порядок"
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                        <Form.Item
                            name="is_active"
                            label="Активна"
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>

                        <Divider>Поля сущности</Divider>
                        <Paragraph type="secondary">
                            Укажите минимум одно поле с ролью
                            «Код» и одно с ролью «Название» —
                            они используются в выпадающих списках
                            задач.
                        </Paragraph>
                        <SchemaFieldsEditor />
                    </Form>
                </Drawer>

                <Modal
                    open={recordOpen}
                    title={
                        editingRecord
                            ? `Запись #${editingRecord.id}`
                            : 'Новая запись'
                    }
                    onCancel={() => setRecordOpen(false)}
                    onOk={saveRecord}
                    width={640}
                    destroyOnClose
                >
                    <Form form={recordForm} layout="vertical">
                        {schemaFields.map(field => (
                            <Form.Item
                                key={field.key}
                                name={field.key}
                                label={
                                    <Space>
                                        <span>{field.label}</span>
                                        {field.role ===
                                        'value' ? (
                                            <Tag color="blue">
                                                код
                                            </Tag>
                                        ) : null}
                                        {field.role ===
                                        'label' ? (
                                            <Tag color="green">
                                                имя
                                            </Tag>
                                        ) : null}
                                    </Space>
                                }
                                rules={
                                    field.required
                                        ? [
                                              {
                                                  required: true,
                                                  message: `Заполните «${field.label}»`
                                              }
                                          ]
                                        : []
                                }
                                valuePropName={
                                    field.value_type ===
                                    'boolean'
                                        ? 'checked'
                                        : 'value'
                                }
                            >
                                <RecordFieldInput field={field} />
                            </Form.Item>
                        ))}
                        <Form.Item
                            name="sort_order"
                            label="Порядок"
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                        <Form.Item
                            name="is_active"
                            label="Активна"
                            valuePropName="checked"
                            initialValue={true}
                        >
                            <Switch />
                        </Form.Item>
                    </Form>
                </Modal>

                <Modal
                    open={importOpen}
                    title="Загрузка данных"
                    onCancel={() => setImportOpen(false)}
                    onOk={runImport}
                    confirmLoading={importing}
                    okText="Импортировать"
                    width={720}
                    destroyOnClose
                >
                    <Space
                        direction="vertical"
                        style={{ width: '100%' }}
                        size={12}
                    >
                        <Text type="secondary">
                            CSV с заголовками по ключам полей.
                            Разделители: запятая, точка с запятой
                            или |. Первая строка — заголовки.
                        </Text>
                        <Text code>{csvHint}</Text>
                        <Radio.Group
                            value={importMode}
                            onChange={e =>
                                setImportMode(e.target.value)
                            }
                            options={[
                                {
                                    value: 'append',
                                    label: 'Добавить / обновить'
                                },
                                {
                                    value: 'replace',
                                    label: 'Заменить все'
                                }
                            ]}
                        />
                        <Upload
                            accept=".csv,.txt"
                            showUploadList={false}
                            beforeUpload={file => {
                                const reader = new FileReader();
                                reader.onload = e => {
                                    setImportText(
                                        String(
                                            e.target?.result ||
                                                ''
                                        )
                                    );
                                    message.success(
                                        `Файл «${file.name}» загружен`
                                    );
                                };
                                reader.readAsText(file);
                                return false;
                            }}
                        >
                            <Button icon={<UploadOutlined />}>
                                Выбрать CSV-файл
                            </Button>
                        </Upload>
                        <TextArea
                            rows={12}
                            value={importText}
                            onChange={e =>
                                setImportText(e.target.value)
                            }
                            placeholder={`${csvHint}\nexample,Пример`}
                        />
                    </Space>
                </Modal>
            </div>
        </Layout>
    );
};

export default TaskManagerDictionariesPage;
